import { Link } from "react-router-dom";
import { Beaker, Blend, Package, Factory, ShieldCheck, Truck, ArrowRight, Check, Leaf, Recycle, Droplets, Sun, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Faq } from "@/components/Faq";

const HOURS = [
  { h: "0–8 hrs", t: "Brief lock", d: "Consultation, formula selection and packaging shortlisting with your launch manager." },
  { h: "8–24 hrs", t: "Lab batch", d: "Chemists compound your sample batch; pH, viscosity and fragrance tuned to brief." },
  { h: "24–48 hrs", t: "Fill & brand", d: "Sample filled into your selected container, labels printed and applied in-house." },
  { h: "48–60 hrs", t: "QA release", d: "Micro, stability snapshot and visual inspection before sign-off." },
  { h: "60–72 hrs", t: "Dispatch", d: "Packed, couriered and tracked to your door anywhere in India." },
];

const CAPACITY = [
  { f: "Creams & Gels", c: "12,000 units / day", p: "Jars · Tubes · Airless" },
  { f: "Serums & Oils", c: "10,000 units / day", p: "Dropper · Pump · Roll-on" },
  { f: "Shampoos & Washes", c: "15,000 units / day", p: "Flip-top · Pump · Pouch" },
  { f: "Lotions & Body", c: "10,000 units / day", p: "Bottles · Tubes" },
  { f: "Sunscreens", c: "6,000 units / day", p: "Tubes · Sticks · Fluids" },
  { f: "Lip & Balms", c: "8,000 units / day", p: "Sticks · Tins · Tubes" },
];

const SIZES = ["10 ml", "30 ml", "50 ml", "100 ml", "200 ml", "500 ml"];
const MACHINES = [
  { m: "Filling Machine 1", type: "Bottle filling", r: { "100 ml": "1,200", "200 ml": "1,200", "500 ml": "1,600" } },
  { m: "Filling Machine 2", type: "Bottle filling", r: { "100 ml": "1,200", "200 ml": "1,200", "500 ml": "1,600" } },
  { m: "Filling Machine 3", type: "Bottle filling", r: { "100 ml": "1,200", "200 ml": "1,200", "500 ml": "1,600" } },
  { m: "Tube Filling", type: "Creams & gels in tubes", r: { "10 ml": "1,300", "30 ml": "1,300", "50 ml": "1,600", "100 ml": "1,600" } },
  { m: "Pneumatic Filling 1", type: "Liquids", r: { "10 ml": "1,000" } },
  { m: "Pneumatic Filling 2", type: "Liquids", r: { "10 ml": "1,000" } },
  { m: "Pneumatic Filling 3", type: "Up to 300 ml", r: { "10 ml": "1,000" } },
];
const BULK = [
  { k: "5 L cans", v: "140 cans / hr" },
  { k: "20 L cans", v: "40 cans / hr" },
];

function FillingLines() {
  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="mfg-filling-lines">
      <Reveal className="grid lg:grid-cols-12 gap-10 mb-12">
        <div className="lg:col-span-7">
          <div className="eyebrow mb-4 !text-[#A68840]">Filling lines</div>
          <h2 className="font-editorial text-5xl lg:text-6xl text-[#1D3A2F] leading-[1.02]">Precision filling, <em className="gold-text">from 10 ml to 20 L.</em></h2>
        </div>
        <p className="lg:col-span-4 lg:col-start-9 self-end text-[#222]/70 leading-relaxed">
          Dedicated bottle, tube, pneumatic and bulk filling lines running in parallel — so every pack size, from travel minis to salon cans, ships on time.
        </p>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[{ k: "8", v: "Filling lines" }, { k: "1,600", v: "Units / hr per line (max)" }, { k: "10 ml–500 ml", v: "Bottle & tube range" }, { k: "5 L · 20 L", v: "Bulk can filling" }].map((s, i) => (
          <Reveal key={s.v} delay={i * 80} className="rounded-[2rem] bg-[#FDFBF7] border border-[#D7D4CF]/70 p-6 gold-hover-border">
            <div className="font-editorial text-4xl gold-text">{s.k}</div>
            <div className="text-[11px] tracking-[0.16em] uppercase text-[#556B57] mt-2">{s.v}</div>
          </Reveal>
        ))}
      </div>
      <Reveal className="rounded-[2rem] bg-white/70 border border-[#C9A961]/35 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="mfg-filling-table">
            <thead>
              <tr className="bg-[#1D3A2F] text-[#F4E4BD]">
                <th className="text-left px-5 py-4 text-[10px] tracking-[0.22em] uppercase font-semibold">Machine</th>
                {SIZES.map((s) => <th key={s} className="px-4 py-4 font-editorial text-lg font-normal text-center">{s}</th>)}
              </tr>
            </thead>
            <tbody>
              {MACHINES.map((row) => (
                <tr key={row.m} className="border-t border-[#C9A961]/20 hover:bg-[#F7F4EE] transition-colors">
                  <td className="px-5 py-4"><div className="font-medium text-[#1D3A2F]">{row.m}</div><div className="text-xs text-[#556B57]">{row.type}</div></td>
                  {SIZES.map((s) => (
                    <td key={s} className="px-4 py-4 text-center">
                      {row.r[s] ? <span className="inline-block px-3 py-1 rounded-full bg-[#C9A961]/15 text-[#1D3A2F] font-semibold whitespace-nowrap">{row.r[s]}<span className="text-[10px] font-normal text-[#556B57]"> /hr</span></span> : <span className="text-[#D7D4CF]">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-[#C9A961]/20 bg-[#F7F4EE]/60">
                <td className="px-5 py-4"><div className="font-medium text-[#1D3A2F]">Bulk Filling (Ecolab)</div><div className="text-xs text-[#556B57]">Large cans</div></td>
                <td colSpan={SIZES.length} className="px-4 py-4">
                  <div className="flex flex-wrap gap-3">
                    {BULK.map((b) => <span key={b.k} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full btn-gold text-xs font-semibold">{b.k} · {b.v}</span>)}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-[#C9A961]/20 text-xs text-[#556B57]">Capacities are per line, per hour. Additional 4-head oil filling line available.</div>
      </Reveal>
    </section>
  );
}

const QC = [
  "Raw-material COA verification", "Identity test on every active", "In-process pH & viscosity", "Particle size check",
  "Microbial limit test (TAMC/TYMC)", "Heavy metals screening", "Accelerated stability 40 °C / 75% RH", "Packaging compatibility",
  "Fill-weight accuracy", "Seal & leak testing", "Label & batch code audit", "Retention samples stored 3 yrs",
];

const ETHICS = [
  { icon: Leaf, t: "Cruelty-free", d: "No animal testing — ever." },
  { icon: Recycle, t: "Recyclable packaging", d: "PCR plastics, glass and mono-material options on request." },
  { icon: Droplets, t: "Water recovery", d: "RO reject reuse and ETP-treated water for utilities." },
  { icon: Sun, t: "Solar assisted", d: "Rooftop solar offsets a share of daily plant energy." },
  { icon: Users, t: "Skilled workforce", d: "160+ trained specialists with ongoing GMP training." },
];

const FAQ = [
  { q: "What is the minimum order quantity?", a: "MOQ starts from 2,000 units per SKU for the Indian market and 5,000 units per SKU for the global market. Samples can be ordered online in any quantity from the Order Sample page." },
  { q: "Can I visit before placing an order?", a: "Yes — you can schedule an office meeting with our team before placing an order. Choose 'Office Visit' when booking a meeting and we'll confirm your slot." },
  { q: "Who owns the formula?", a: "For custom R&D projects, formula ownership terms are agreed in writing before development. Catalogue formulas are licensed for your private label." },
  { q: "How do you ensure batch-to-batch consistency?", a: "Every batch follows a locked master formula record, in-process checks and final QA release with retained samples for traceability." },
];

const STEPS = [
  {
    icon: Beaker,
    n: "01",
    t: "R&D Lab",
    d: "In-house chemists develop 40+ new formulas every quarter — HPLC-tested, stability-verified, market-ready.",
  },
  {
    icon: Blend,
    n: "02",
    t: "Precision Mixing",
    d: "Vacuum-emulsification tanks up to 5,000L. Every batch monitored for particle size, viscosity, pH.",
  },
  {
    icon: Package,
    n: "03",
    t: "Filling",
    d: "Automated bottle, tube, jar and airless pump lines. 30,000 units/day capacity per shift.",
  },
  {
    icon: Factory,
    n: "04",
    t: "Packaging",
    d: "In-house label design, foil stamp, emboss, secondary cartoning and shrink-sleeve stations.",
  },
  {
    icon: ShieldCheck,
    n: "05",
    t: "Quality Assurance",
    d: "Every SKU sampled for microbiology, heavy metals and stability. CDSCO + GMP compliant, ISO certified.",
  },
  {
    icon: Truck,
    n: "06",
    t: "Dispatch",
    d: "Bonded warehouse, temperature-controlled logistics, export documentation for 30+ countries.",
  },
];

export default function Manufacturing() {
  return (
    <div className="pt-32 pb-24">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-8">
            <div className="eyebrow mb-5">Manufacturing</div>
            <h1 className="font-editorial text-5xl md:text-8xl text-[#1D3A2F] leading-[0.95]">
              World-class
              <br />
              <em className="gold-text">manufacturing facility.</em>
            </h1>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <p className="text-lg text-[#222222]/70 leading-relaxed">
              A 40,000 sq. ft. GMP-certified facility engineered for premium
              cosmetic manufacturing. Every SKU passes six precision stages.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {[
            { k: "40,000", v: "Sq. Ft. Facility" },
            { k: "5,000L", v: "Max Batch Size" },
            { k: "GMP", v: "ISO Certified" },
            { k: "72Hr", v: "Sample Turnaround" },
          ].map((s) => (
            <div key={s.v} className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-8">
              <div className="font-editorial text-4xl text-[#1D3A2F]">{s.k}</div>
              <div className="text-xs tracking-[0.18em] uppercase text-[#556B57] mt-3">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className="bg-[#FDFBF7] border-y border-[#D7D4CF]/50 py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-4">Process</div>
          <h2 className="font-editorial text-5xl text-[#1D3A2F] mb-12">
            Six stages. One promise.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.n}
                  className="rounded-3xl bg-[#F7F4EE] border border-[#D7D4CF]/50 p-8 hover-lift"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-full bg-[#FDFBF7] border border-[#D7D4CF] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#1D3A2F]" strokeWidth={1.4} />
                    </div>
                    <span className="eyebrow">{s.n}</span>
                  </div>
                  <h3 className="font-editorial text-3xl text-[#1D3A2F] mt-8">
                    {s.t}
                  </h3>
                  <p className="text-sm text-[#222222]/70 mt-3 leading-relaxed">
                    {s.d}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 72-hour timeline */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="mfg-72hr-timeline">
        <Reveal className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4 !text-[#A68840]">Inside the 72 hours</div>
            <h2 className="font-editorial text-5xl lg:text-6xl text-[#1D3A2F] leading-[1.02]">How your sample is <em className="gold-text">made, hour by hour.</em></h2>
          </div>
          <p className="lg:col-span-4 lg:col-start-9 self-end text-[#222]/70 leading-relaxed">A dedicated launch cell runs your project in parallel tracks, so nothing waits in a queue.</p>
        </Reveal>
        <div className="grid md:grid-cols-5 gap-4">
          {HOURS.map((s, i) => (
            <Reveal key={s.h} delay={i * 90} className="group relative rounded-[2rem] bg-[#FDFBF7] border border-[#D7D4CF]/70 p-7 gold-hover-border">
              <div className="font-editorial text-3xl gold-text">{s.h}</div>
              <div className="h-px bg-[#C9A961]/40 my-5 origin-left transition-transform duration-700 group-hover:scale-x-150" />
              <h3 className="font-editorial text-2xl text-[#1D3A2F]">{s.t}</h3>
              <p className="text-sm text-[#222]/70 mt-2 leading-relaxed">{s.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Capacity by format */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <Reveal className="rounded-[2.5rem] bg-[#1D3A2F] text-[#FDFBF7] p-8 md:p-14 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#C9A961]/20 blur-3xl animate-drift" />
          <div className="relative grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#E6D5A4] mb-4">Capacity by format</div>
              <h2 className="font-editorial text-4xl lg:text-5xl leading-[1.05]">Built for <em className="gold-text">scale.</em></h2>
              <p className="mt-5 text-sm text-[#FDFBF7]/70 leading-relaxed">Dedicated lines per format mean your reorders never wait behind someone else's batch.</p>
            </div>
            <div className="lg:col-span-8 divide-y divide-[#FDFBF7]/10" data-testid="mfg-capacity-table">
              {CAPACITY.map((r) => (
                <div key={r.f} className="grid grid-cols-2 md:grid-cols-3 gap-4 py-5 transition-[padding] duration-500 hover:pl-3">
                  <span className="font-editorial text-2xl">{r.f}</span>
                  <span className="gold-text font-semibold text-right md:text-left">{r.c}</span>
                  <span className="text-sm text-[#FDFBF7]/60 col-span-2 md:col-span-1 md:text-right">{r.p}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <FillingLines />

      {/* QC checkpoints */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <Reveal className="mb-12">
          <div className="eyebrow mb-4 !text-[#A68840]">Quality control</div>
          <h2 className="font-editorial text-5xl lg:text-6xl text-[#1D3A2F] leading-[1.02]">12 checkpoints. <em className="gold-text">Zero shortcuts.</em></h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mfg-qc-grid">
          {QC.map((q, i) => (
            <Reveal key={q} delay={i * 40} className="group flex items-center gap-4 rounded-2xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-5 gold-hover-border">
              <span className="w-9 h-9 shrink-0 rounded-full border gold-border flex items-center justify-center font-editorial text-[#A68840] transition-colors duration-500 group-hover:bg-[#C9A961] group-hover:text-[#1D3A2F]">{i + 1}</span>
              <span className="text-sm text-[#1D3A2F]">{q}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Facility Spec Sheet */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-6">
            <div className="eyebrow mb-4">Facility Spec Sheet</div>
            <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.05]">
              Numbers that
              <br />
              <em className="gold-text">move the industry.</em>
            </h2>
          </div>
          <div className="lg:col-span-6 flex items-end">
            <p className="text-[#222222]/70 leading-relaxed">
              A precision-engineered ecosystem where every square foot is
              measured, every batch is tracked, every ingredient is
              documented, and every SKU passes 3-stage stability testing
              before it leaves the door.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { label: "Total facility", val: "40,000 sq ft" },
            { label: "R&D lab", val: "6,000 sq ft" },
            { label: "Blending capacity", val: "5,000 L / batch" },
            { label: "Filling speed", val: "8,000 units / hr" },
            { label: "Daily output", val: "40,000+ SKUs" },
            { label: "Warehouse", val: "12,000 sq ft" },
            { label: "Cold storage", val: "800 sq ft · 2–8 °C" },
            { label: "Team size", val: "160+ specialists" },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-2xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-5 flex items-center justify-between"
            >
              <span className="text-sm text-[#556B57]">{s.label}</span>
              <span className="font-editorial text-2xl text-[#1D3A2F]">
                {s.val}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* R&D Capability */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="rounded-3xl bg-[#1D3A2F] text-[#FDFBF7] p-10 md:p-14 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="eyebrow text-[#FDFBF7]/60 mb-4">R&amp;D Lab</div>
            <h3 className="font-editorial text-4xl leading-[1.05]">
              A formulation
              <br />
              <em className="text-[#D7D4CF]">brain trust.</em>
            </h3>
            <p className="mt-6 text-[#FDFBF7]/75 leading-relaxed max-w-md">
              18 senior chemists — cross-trained in cosmetology, biochemistry,
              Ayurveda and Korean K-beauty science. Every custom formula
              passes 3-stage stability testing before manufacturing.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { k: "600+", v: "Proven formulas" },
                { k: "40+", v: "New formulas / quarter" },
                { k: "18", v: "Senior chemists" },
                { k: "3-stage", v: "Stability testing" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-editorial text-3xl text-[#FDFBF7]">
                    {s.k}
                  </div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[#FDFBF7]/60 mt-1">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="eyebrow text-[#FDFBF7]/60 mb-4">Equipment stack</div>
            <ul className="space-y-3 text-sm">
              {[
                "HPLC — active ingredient assay and purity profiling",
                "GC-MS — impurity, residual solvent and trace metals",
                "Rheometer — viscosity, texture, yield stress",
                "Klimatest chamber — accelerated stability (40 °C / 75 % RH)",
                "pH · conductivity · refractive index instruments",
                "Microbial station — TAMC, TYMC, pathogen screening",
                "Vacuum emulsification homogenizer up to 5,000 L",
                "Nano-emulsion mixer — peptide + liposome delivery",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-[#D7D4CF] mt-0.5 flex-shrink-0" />
                  <span className="text-[#FDFBF7]/85">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Ingredient Sourcing */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-6">
            <div className="eyebrow mb-4">Ingredient Sourcing</div>
            <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.05]">
              Global grade,
              <br />
              <em className="gold-text">Indian precision.</em>
            </h2>
          </div>
          <div className="lg:col-span-6 flex items-end">
            <p className="text-[#222222]/70 leading-relaxed">
              Every raw material is COA-verified at the point of intake. We
              trace ingredients back to their origin farms, forests and
              cold-press facilities — so your brand story is never a
              marketing invention.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[
            { r: "France", i: "Peptides · Silk Protein" },
            { r: "South Korea", i: "PDRN · Exosomes · Bio-ferment" },
            { r: "Japan", i: "Rice Water · Kombucha" },
            { r: "Switzerland", i: "Retinal · Vit-C stabilizers" },
            { r: "India · Kerala", i: "Coconut · Ayurveda" },
            { r: "India · Himalayas", i: "Botanical Extracts" },
            { r: "USA", i: "Niacinamide · Actives" },
            { r: "Morocco", i: "Argan · Rosehip" },
          ].map((s) => (
            <div
              key={s.r}
              className="rounded-2xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-5"
            >
              <div className="font-editorial text-xl text-[#1D3A2F]">
                {s.r}
              </div>
              <div className="text-xs text-[#556B57] mt-2">{s.i}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Sustainability */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <Reveal className="mb-10">
          <div className="eyebrow mb-4 !text-[#A68840]">Responsible manufacturing</div>
          <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.05]">Beauty that <em className="gold-text">does no harm.</em></h2>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {ETHICS.map((e, i) => {
            const Icon = e.icon;
            return (
              <Reveal key={e.t} delay={i * 70} className="group rounded-[2rem] hero-mesh border border-[#C9A961]/30 p-6 gold-hover-border">
                <Icon className="w-6 h-6 text-[#A68840] mb-6 transition-transform duration-500 group-hover:scale-125" strokeWidth={1.4} />
                <h3 className="font-editorial text-2xl text-[#1D3A2F]">{e.t}</h3>
                <p className="text-xs text-[#222]/70 mt-2 leading-relaxed">{e.d}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="eyebrow mb-4 !text-[#A68840]">FAQ</div>
          <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.02]">Factory <em className="gold-text">questions.</em></h2>
        </div>
        <div className="lg:col-span-8"><Faq items={FAQ} testid="mfg-faq" /></div>
      </section>

      {/* Certifications */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="rounded-3xl bg-[#1D3A2F] text-[#FDFBF7] p-10 md:p-14">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow text-[#FDFBF7]/60 mb-4">
                Certifications
              </div>
              <h3 className="font-editorial text-4xl leading-[1.05]">
                Global-standard
                <br />
                compliance.
              </h3>
              <p className="text-sm text-[#FDFBF7]/70 mt-4 max-w-md">
                From CDSCO in India to CPNP in the EU — we prepare every export
                dossier so your launch is never blocked by paperwork.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3">
              {["GMP", "ISO Certified", "CDSCO", "FDA Export", "CPNP EU", "Cruelty-Free"].map(
                (c) => (
                  <div
                    key={c}
                    className="rounded-2xl border border-[#FDFBF7]/15 py-6 text-center font-editorial text-xl"
                  >
                    {c}
                  </div>
                ),
              )}
            </div>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/contact#book-meeting"
              data-testid="mfg-book-visit"
              className="btn-gold inline-flex items-center gap-2 py-4 px-7 rounded-full text-[12px] tracking-[0.18em] uppercase font-bold"
            >
              Book an Office Visit <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/order-sample"
              className="inline-flex items-center gap-2 py-4 px-7 rounded-full border border-[#FDFBF7]/40 text-[#FDFBF7] text-[12px] tracking-[0.18em] uppercase hover:border-[#FDFBF7] transition"
            >
              Order Sample
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
